import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { SpendingChart } from "@/components/SpendingChart";
import { PremiumLock } from "@/components/PremiumLock";
import { getCategorie, CATEGORII } from "@/lib/categories";
import { formateazaRON } from "@/lib/settlement";
import { ExpenseListItem } from "@/components/group-detail/ExpenseListItem";
import { PaymentListItem } from "@/components/group-detail/PaymentListItem";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formateazaData = (iso) =>
  new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "short" });

// Tab-ul „Cheltuieli": grafic (Premium), export CSV/PDF (Premium), filtru pe
// categorii și istoricul cheltuieli + plăți de decontare.
export function ExpensesTab({ group, isPremium, onEdit, onDeleteExpense, onDeletePayment }) {
  const [filtruCategorie, setFiltruCategorie] = useState("toate");

  const numeMembru = (id) => group.members.find((m) => m.id === id)?.name || "?";
  const total = group.expenses.reduce((s, e) => s + e.amount, 0);
  const payments = group.payments || [];

  const istoric = [
    ...group.expenses.map((e) => ({ ...e, type: "expense" })),
    ...payments.map((p) => ({ ...p, type: "payment" })),
  ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const totaluriCategorii = {};
  group.expenses.forEach((e) => {
    const c = e.category || "altele";
    totaluriCategorii[c] = (totaluriCategorii[c] || 0) + e.amount;
  });
  const categoriiActive = CATEGORII.filter((c) => totaluriCategorii[c.id]);

  const istoricFiltrat =
    filtruCategorie === "toate"
      ? istoric
      : istoric.filter((it) => it.type === "expense" && it.category === filtruCategorie);

  // Deschide bonul/factura atașat (descărcat ca blob, cu autentificare pe cookie).
  const openReceipt = async (receiptId) => {
    try {
      const r = await axios.get(`${API}/upload/receipt/${receiptId}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Nu am putut deschide bonul");
    }
  };

  const exportCSV = () => {
    const linii = [["Descriere", "Suma (RON)", "Platit de", "Categorie", "Data"]];
    group.expenses.forEach((e) => {
      linii.push([
        e.description,
        e.amount.toFixed(2),
        numeMembru(e.paidBy),
        getCategorie(e.category).label,
        formateazaData(e.createdAt),
      ]);
    });
    const csv = linii.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chit-${group.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exportat");
  };

  const exportPDF = () => {
    // Escapăm textul controlat de utilizatori (securitate — evităm injectarea HTML).
    const esc = (s) =>
      String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const randuri = group.expenses
      .map(
        (e) =>
          `<tr><td>${esc(e.description)}</td><td>${esc(formateazaRON(e.amount))}</td><td>${esc(
            numeMembru(e.paidBy)
          )}</td><td>${esc(getCategorie(e.category).label)}</td><td>${esc(formateazaData(e.createdAt))}</td></tr>`
      )
      .join("");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>Chit — ${esc(group.name)}</title>
      <style>body{font-family:sans-serif;padding:24px}h1{color:#059669}
      table{width:100%;border-collapse:collapse;margin-top:16px}
      th,td{text-align:left;padding:8px;border-bottom:1px solid #eee;font-size:14px}</style>
      </head><body><h1>Chit — ${esc(group.name)}</h1>
      <p>Total: ${esc(formateazaRON(total))}</p>
      <table><thead><tr><th>Descriere</th><th>Suma</th><th>Plătit de</th><th>Categorie</th><th>Data</th></tr></thead>
      <tbody>${randuri}</tbody></table></body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div className="space-y-3">
      {group.expenses.length > 0 &&
        (isPremium ? (
          <SpendingChart expenses={group.expenses} members={group.members} />
        ) : (
          <PremiumLock
            testid="chart-lock"
            title="Statistici și grafice"
            desc="Vezi cât s-a cheltuit pe categorii și evoluția în timp cu Chit Premium."
          />
        ))}
      {group.expenses.length > 0 &&
        (isPremium ? (
          <div className="flex gap-2">
            <button
              data-testid="export-csv-btn"
              onClick={exportCSV}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold transition-colors"
            >
              <Download className="h-4 w-4" strokeWidth={2.5} /> CSV
            </button>
            <button
              data-testid="export-pdf-btn"
              onClick={exportPDF}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold transition-colors"
            >
              <Download className="h-4 w-4" strokeWidth={2.5} /> PDF
            </button>
          </div>
        ) : (
          <PremiumLock testid="export-lock" title="Export CSV / PDF" desc="Descarcă cheltuielile grupului cu Chit Premium." />
        ))}
      {categoriiActive.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          <button
            data-testid="filter-toate"
            onClick={() => setFiltruCategorie("toate")}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              filtruCategorie === "toate" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"
            }`}
          >
            Toate · {formateazaRON(total)}
          </button>
          {categoriiActive.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-${c.id}`}
              onClick={() => setFiltruCategorie(c.id)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                filtruCategorie === c.id ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              {c.label} · {formateazaRON(totaluriCategorii[c.id])}
            </button>
          ))}
        </div>
      )}
      {istoric.length === 0 ? (
        <p className="text-center text-slate-400 text-sm mt-16">
          Nicio cheltuială încă.<br />Apasă „Adaugă cheltuială&rdquo;.
        </p>
      ) : istoricFiltrat.length === 0 ? (
        <p className="text-center text-slate-400 text-sm mt-10">Nicio cheltuială în această categorie.</p>
      ) : (
        istoricFiltrat.map((item) =>
          item.type === "payment" ? (
            <PaymentListItem
              key={item.id}
              item={item}
              numeMembru={numeMembru}
              formateazaData={formateazaData}
              onDelete={(id) => {
                onDeletePayment(id);
                toast.success("Plată anulată");
              }}
            />
          ) : (
            <ExpenseListItem
              key={item.id}
              item={item}
              numeMembru={numeMembru}
              formateazaData={formateazaData}
              onOpenReceipt={openReceipt}
              onEdit={onEdit}
              onDelete={(id) => {
                onDeleteExpense(id);
                toast.success("Cheltuială ștearsă");
              }}
            />
          )
        )
      )}
    </div>
  );
}
